import {
  Box,
  Fade,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Input from "../../components/ui/Input";

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  AUDIT_ACTION_LABELS,
  AUDIT_CATEGORIES,
  getActionsByCategory,
} from "../../data/auditActions";
import api from "../../services/api";
import { useIntl } from "../../contexts/IntlContext";
import { TableEmptyState } from "../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../components/table/TableSkeletonRows";
import { StandardTablePagination } from "../../components/table/StandardTablePagination";
import { PageHeader } from "../../components/PageHeader";
import DatePickerField from "../../components/Dashboard/DatePickerField";
import Select from "../../components/ui/Select";
import FiltersBar from "../../components/ui/FiltersBar";

interface AuditLogRow {
  eventTime: string;
  action: string | null;
  entityType: string | null;
  entityId: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  userRole: string | null;
  sourceIPAddress: string;
  method: string;
  path: string;
  statusCode: number;
  duration: number;
  eventSource: string;
}

export function AuditLogs() {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tab, setTab] = useState(0);

  const [data, setData] = useState<AuditLogRow[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [category, setCategory] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [debouncedUserEmail, setDebouncedUserEmail] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const eventSource = tab === 0 ? 'critical_action' : undefined;

  const actionsFiltered = category ? getActionsByCategory(category) : [];

  const activeFilterCount = [debouncedUserEmail, category, selectedAction, dateFrom, dateTo].filter(
    Boolean
  ).length;

  function buildParams() {
    const params: Record<string, any> = {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    };

    if (eventSource) params.eventSource = eventSource;
    if (selectedAction) params.action = selectedAction;
    if (debouncedUserEmail.trim()) params.userEmail = debouncedUserEmail.trim();
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    return params;
  }

  async function fetchData() {
    setLoading(true);
    try {
      const response = await api.get('/app/admin/audit-logs', {
        params: buildParams(),
      });
      setData(response.data.data);
      setCount(response.data.count);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        toast.error(intl.formatMessage({ id: "auditLogs.sessionExpired" }));
      } else if (error?.response?.status !== 400) {
        toast.error(intl.formatMessage({ id: "messages.connectionError" }));
      }
    } finally {
      setLoading(false);
    }
  }

  // Filtros são aplicados automaticamente (com debounce no campo de e-mail),
  // alinhado ao padrão das demais listagens administrativas do sistema.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, tab, selectedAction, debouncedUserEmail, dateFrom, dateTo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserEmail(userEmail);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [userEmail]);

  function handleCategoryChange(value: string) {
    setCategory(value);
    setSelectedAction("");
  }

  function handleSelectedActionChange(value: string) {
    setSelectedAction(value);
    setPage(0);
  }

  function handleDateFromChange(value: string) {
    setDateFrom(value);
    setPage(0);
  }

  function handleDateToChange(value: string) {
    setDateTo(value);
    setPage(0);
  }

  function handleTabChange(_: any, newValue: number) {
    setTab(newValue);
    setPage(0);
    // Categoria/Ação pertencem a uma taxonomia específica da aba "Atividades" e
    // podem não fazer sentido em "Requisições" — são resetadas ao trocar de aba.
    // E-mail e período são preservados: continuam válidos nas duas abas.
    setSelectedAction("");
    setCategory("");
  }

  function formatDateTime(iso: string) {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const actionLabel = (action: string | null) =>
    action ? AUDIT_ACTION_LABELS[action]?.label || action : '-';

  const visibleColumnCount =
    3 + (tab === 0 ? (isMobile ? 1 : 2) : (isMobile ? 3 : 4));

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <PageHeader
        title={intl.formatMessage({ id: "auditLogs.title" })}
        loading={loading}
        sx={{ px: 2, py: 1 }}
      />

      <FiltersBar activeFilterCount={activeFilterCount}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          columnGap: 2,
          rowGap: 2,
        }}
      >
        <Input
          label={intl.formatMessage({ id: "auditLogs.filterUser" })}
          placeholder={intl.formatMessage({ id: "auditLogs.placeholderEmail" })}
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          sx={{ width: { xs: "100%", sm: 220 } }}
        />

        <Select
          label={intl.formatMessage({ id: "auditLogs.filterCategory" })}
          value={category}
          onChange={(e) => handleCategoryChange(e.target.value)}
          sx={{ width: { xs: "100%", sm: 200 } }}
        >
          {AUDIT_CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          label={intl.formatMessage({ id: "auditLogs.filterAction" })}
          value={selectedAction}
          onChange={(e) => handleSelectedActionChange(e.target.value)}
          disabled={!category}
          helperText={!category ? intl.formatMessage({ id: "auditLogs.selectCategoryFirst" }) : undefined}
          sx={{ width: { xs: "100%", sm: 220 } }}
        >
          <MenuItem value="">{intl.formatMessage({ id: "auditLogs.allActions" })}</MenuItem>
          {actionsFiltered.map((a) => (
            <MenuItem key={a.value} value={a.value}>
              {a.label}
            </MenuItem>
          ))}
        </Select>

        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <DatePickerField
            label={intl.formatMessage({ id: "auditLogs.filterDateFrom" })}
            value={dateFrom}
            onChange={handleDateFromChange}
            onToday={() => handleDateFromChange(new Date().toISOString().split("T")[0])}
            onClear={() => handleDateFromChange("")}
            minWidth={160}
            fullWidth={isMobile}
            sx={{ width: { xs: "100%", sm: "auto" } }}
            size="small"
          />

          <DatePickerField
            label={intl.formatMessage({ id: "auditLogs.filterDateTo" })}
            value={dateTo}
            onChange={handleDateToChange}
            onToday={() => handleDateToChange(new Date().toISOString().split("T")[0])}
            onClear={() => handleDateToChange("")}
            minWidth={160}
            fullWidth={isMobile}
            sx={{ width: { xs: "100%", sm: "auto" } }}
            size="small"
          />
        </Stack>
      </Box>
      </FiltersBar>

      <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2 }}>
        <Tab label={intl.formatMessage({ id: "auditLogs.tabActivities" })} />
        <Tab label={intl.formatMessage({ id: "auditLogs.tabRequests" })} />
      </Tabs>

      <TableContainer sx={{ maxHeight: '65vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 150 }}>{intl.formatMessage({ id: "auditLogs.when" })}</TableCell>
              <TableCell sx={{ minWidth: 200 }}>{intl.formatMessage({ id: "auditLogs.who" })}</TableCell>
              <TableCell sx={{ minWidth: 250 }}>{intl.formatMessage({ id: "auditLogs.what" })}</TableCell>
              {tab === 0 && <TableCell sx={{ minWidth: 100 }}>{intl.formatMessage({ id: "auditLogs.entity" })}</TableCell>}
              {tab === 0 && !isMobile && <TableCell sx={{ minWidth: 100 }}>{intl.formatMessage({ id: "auditLogs.entityId" })}</TableCell>}
              {tab === 1 && <TableCell sx={{ minWidth: 80 }}>{intl.formatMessage({ id: "auditLogs.method" })}</TableCell>}
              {tab === 1 && <TableCell sx={{ minWidth: 200 }}>{intl.formatMessage({ id: "auditLogs.route" })}</TableCell>}
              {tab === 1 && <TableCell sx={{ minWidth: 80 }}>{intl.formatMessage({ id: "auditLogs.statusCode" })}</TableCell>}
              {tab === 1 && !isMobile && <TableCell sx={{ minWidth: 80 }}>{intl.formatMessage({ id: "auditLogs.duration" })}</TableCell>}
            </TableRow>
          </TableHead>
          <Fade in key={loading ? `loading-${tab}` : `loaded-${tab}`} timeout={200}>
            <TableBody>
              {loading && (
                <TableSkeletonRows
                  rows={rowsPerPage > 10 ? 10 : rowsPerPage}
                  columns={visibleColumnCount}
                />
              )}
              {!loading && data.map((row, i) => (
                <TableRow hover key={i}>
                  <TableCell>{formatDateTime(row.eventTime)}</TableCell>
                  <TableCell>{row.userEmail || row.userName || '-'}</TableCell>
                  <TableCell>{actionLabel(row.action)}</TableCell>
                  {tab === 0 && <TableCell>{row.entityType || '-'}</TableCell>}
                  {tab === 0 && !isMobile && <TableCell>{row.entityId || '-'}</TableCell>}
                  {tab === 1 && <TableCell>{row.method}</TableCell>}
                  {tab === 1 && <TableCell>{row.path}</TableCell>}
                  {tab === 1 && <TableCell>{row.statusCode}</TableCell>}
                  {tab === 1 && !isMobile && <TableCell>{row.duration}ms</TableCell>}
                </TableRow>
              ))}
              {!loading && data.length === 0 && (
                <TableEmptyState colSpan={visibleColumnCount}>
                  {intl.formatMessage({ id: "auditLogs.empty" })}
                </TableEmptyState>
              )}
            </TableBody>
          </Fade>
        </Table>
      </TableContainer>

      <StandardTablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
      />
    </Paper>
  );
}
